(() => {
  const root = document.querySelector('[data-va-pdp-model]');
  if (!root) return;

  const gallery = root.querySelector('[data-va-gallery]');
  const thumbnails = root.querySelector('[data-va-thumbnails]');
  const counter = root.querySelector('[data-va-gallery-counter]');
  const previous = root.querySelector('[data-va-gallery-prev]');
  const next = root.querySelector('[data-va-gallery-next]');
  const slides = Array.from(root.querySelectorAll('[data-va-media]'));
  const colorIndex = Number(root.dataset.vaColorIndex || 0);
  const defaultColor = root.dataset.vaDefaultColor || '';
  const mobileQuery = window.matchMedia('(max-width: 899px)');
  const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  const sectionId = root.dataset.sectionId;

  if (!gallery) return;

  slides.forEach(slide => {
    const image = slide.querySelector('img[data-va-original-src]');
    if (!image) return;
    const restoreOriginal = () => {
      if (image.src === image.dataset.vaOriginalSrc) return;
      image.src = image.dataset.vaOriginalSrc;
    };
    image.addEventListener('error', restoreOriginal, {once: true});
    if (image.complete && !image.naturalWidth) restoreOriginal();
  });

  let visibleSlides = [];
  let activeIndex = 0;
  let galleryObserver;
  let lightbox;

  const scrollBehavior = () => reducedMotionQuery.matches ? 'auto' : 'smooth';

  const getPickerElements = () => Array.from(root.querySelectorAll('variant-selects'));

  const getMainPicker = () => root.querySelector('variant-selects:not([data-sticky])') || root.querySelector('variant-selects');

  const getColorFieldset = picker => {
    if (!picker || colorIndex < 0) return null;
    return picker.querySelector(`fieldset[data-index="option${colorIndex + 1}"]`) || Array.from(picker.querySelectorAll('fieldset')).find(fieldset => fieldset.dataset.handle === 'color');
  };

  const readColorFromPicker = () => {
    const fieldset = getColorFieldset(getMainPicker());
    const checked = fieldset?.querySelector('input[type="radio"]:checked');
    if (checked) return checked.value;

    const select = fieldset?.querySelector('select');
    return select?.value || defaultColor;
  };

  const getSlidesForColor = color => {
    if (!color || color === 'unmapped') return slides;
    // Unknown groups remain visible: a newly published product must never have an empty gallery.
    const shared = slides.filter(slide => !slide.dataset.vaColor || slide.dataset.vaColor === 'unmapped');
    const matching = slides.filter(slide => slide.dataset.vaColor === color);
    return matching.length ? [...matching, ...shared] : slides;
  };

  const updateCounter = () => {
    if (counter) counter.textContent = `${visibleSlides.length ? activeIndex + 1 : 0} / ${visibleSlides.length}`;
    if (previous) previous.disabled = visibleSlides.length < 2;
    if (next) next.disabled = visibleSlides.length < 2;
  };

  const updateThumbnails = () => {
    if (!thumbnails) return;
    thumbnails.replaceChildren();
    visibleSlides.forEach((slide, index) => {
      const source = slide.querySelector('img');
      const button = document.createElement('button');
      const image = document.createElement('img');
      button.type = 'button';
      button.className = 'va-pdp-model__thumbnail';
      button.setAttribute('aria-label', `Go to product image ${index + 1}`);
      button.setAttribute('aria-current', String(index === activeIndex));
      image.src = source?.currentSrc || source?.src || slide.dataset.vaThumbnail || '';
      image.alt = '';
      image.loading = 'lazy';
      button.append(image);
      button.addEventListener('click', () => goTo(index));
      thumbnails.append(button);
    });
  };

  const updateActiveState = () => {
    slides.forEach(slide => {
      const index = visibleSlides.indexOf(slide);
      slide.classList.toggle('is-active', index >= 0 && index === activeIndex);
    });
    thumbnails?.querySelectorAll('button').forEach((button, index) => {
      button.setAttribute('aria-current', String(index === activeIndex));
    });
    updateCounter();
  };

  const rebuildObserver = () => {
    galleryObserver?.disconnect();
    galleryObserver = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        const index = visibleSlides.indexOf(entry.target);
        if (index < 0) return;
        activeIndex = index;
        updateActiveState();
      });
    }, {
      root: gallery,
      threshold: 0.55
    });
    visibleSlides.forEach(slide => galleryObserver.observe(slide));
  };

  const applyColor = color => {
    const nextSlides = getSlidesForColor(color);
    visibleSlides = nextSlides
      .slice()
      .sort((a, b) => Number(b.dataset.vaColor === color) - Number(a.dataset.vaColor === color) || Number(a.dataset.vaOrder || 999) - Number(b.dataset.vaOrder || 999));

    const visibleSet = new Set(visibleSlides);
    slides.forEach(slide => {
      const isVisible = visibleSet.has(slide);
      slide.hidden = !isVisible;
      slide.setAttribute('aria-hidden', String(!isVisible));
      slide.style.order = isVisible ? String(visibleSlides.indexOf(slide)) : '999';
    });

    activeIndex = 0;
    gallery.scrollTo({left: 0, top: 0, behavior: 'auto'});
    updateThumbnails();
    updateActiveState();
    rebuildObserver();
  };

  let mobileMode = mobileQuery.matches;
  window.addEventListener('resize', () => {
    const nextMobileMode = mobileQuery.matches;
    if (nextMobileMode !== mobileMode) {
      mobileMode = nextMobileMode;
      applyColor(readColorFromPicker() || defaultColor);
      return;
    }
    rebuildObserver();
  });

  function goTo(index) {
    if (!visibleSlides.length) return;
    activeIndex = (index + visibleSlides.length) % visibleSlides.length;
    const target = visibleSlides[activeIndex];
    if (mobileQuery.matches) {
      gallery.scrollTo({left: target.offsetLeft, behavior: scrollBehavior()});
    } else {
      const galleryRect = gallery.getBoundingClientRect();
      const targetRect = target.getBoundingClientRect();
      const top = Math.max(0, gallery.scrollTop + targetRect.top - galleryRect.top);
      gallery.scrollTo({top, behavior: scrollBehavior()});
    }
    updateActiveState();
  }

  const setupLightbox = () => {
    if (root.dataset.vaLightbox === 'false' || !HTMLDialogElement.prototype.showModal) return;

    lightbox = document.createElement('dialog');
    lightbox.dataset.vaLightbox = 'true';
    lightbox.innerHTML = '<button type="button" class="va-pdp-model__lightbox-close" aria-label="Close image">×</button><img alt="">';
    root.append(lightbox);
    const close = lightbox.querySelector('button');
    const image = lightbox.querySelector('img');
    close.addEventListener('click', () => lightbox.close());
    lightbox.addEventListener('click', event => {
      if (event.target === lightbox) lightbox.close();
    });
    slides.forEach(slide => {
      slide.querySelector('[data-va-zoom]')?.addEventListener('click', () => {
        const source = slide.querySelector('img');
        image.src = source?.currentSrc || source?.src || slide.dataset.vaThumbnail || '';
        image.alt = source?.alt || '';
        lightbox.showModal();
      });
    });
  };

  const setupQuantitySync = () => {
    const stickyInput = root.querySelector('[data-va-quantity-sticky]');
    const stickyQuantity = stickyInput?.closest('[data-va-quantity-sync]');
    const quantityContainer = document.getElementById(`quantity-${sectionId}`);
    if (!stickyInput || !stickyQuantity || !quantityContainer) return;

    const getMainInput = () => document.getElementById(`Quantity-${sectionId}`);

    const copyRule = (source, target, attribute) => {
      if (source.hasAttribute(attribute)) {
        target.setAttribute(attribute, source.getAttribute(attribute));
      } else {
        target.removeAttribute(attribute);
      }
    };

    const normaliseValue = (input, value) => {
      let next = Number(value);
      if (!Number.isFinite(next)) next = Number(input.min) || 1;
      if (input.min) next = Math.max(next, Number(input.min));
      if (input.max) next = Math.min(next, Number(input.max));
      return String(next);
    };

    const syncStickyFromMain = () => {
      const mainInput = getMainInput();
      if (!mainInput) {
        stickyQuantity.hidden = true;
        return;
      }

      stickyQuantity.hidden = false;
      ['min', 'max', 'step'].forEach(attribute => copyRule(mainInput, stickyInput, attribute));
      const stickySelector = stickyInput.closest('quantity-selector');
      if (stickySelector) {
        stickySelector.step = stickyInput.getAttribute('step');
        stickySelector.validateQtyRules?.();
      }
      const value = normaliseValue(mainInput, mainInput.value);
      if (mainInput.value !== value) mainInput.value = value;
      stickyInput.value = value;
    };

    const syncMainFromSticky = () => {
      const mainInput = getMainInput();
      if (!mainInput) return;

      const value = normaliseValue(mainInput, stickyInput.value);
      stickyInput.value = value;
      mainInput.value = value;
      mainInput.dispatchEvent(new Event('input', {bubbles: true}));
      mainInput.dispatchEvent(new Event('change', {bubbles: true}));
    };

    stickyInput.addEventListener('input', syncMainFromSticky);
    stickyInput.addEventListener('change', syncMainFromSticky);
    document.addEventListener('input', event => {
      if (event.target?.id === `Quantity-${sectionId}`) syncStickyFromMain();
    });
    document.addEventListener('change', event => {
      if (event.target?.id === `Quantity-${sectionId}`) syncStickyFromMain();
    });

    const observer = new MutationObserver(syncStickyFromMain);
    observer.observe(quantityContainer, {childList: true, subtree: true});
    document.addEventListener('product:variant-change', syncStickyFromMain);
    syncStickyFromMain();
  };

  const setupStickyOptions = () => {
    const sticky = root.querySelector('product-add-to-cart-sticky');
    const trigger = sticky?.querySelector('.product-add-to-cart-sticky--inner');
    const content = sticky?.querySelector('.product-add-to-cart-sticky--content');
    if (!sticky || !trigger || !content) return;

    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'va-pdp-sticky-close';
    close.setAttribute('aria-label', 'Close product options');
    close.textContent = '×';
    content.prepend(close);
    content.id = `va-pdp-sticky-options-${sectionId}`;
    content.setAttribute('role', 'dialog');
    content.setAttribute('aria-modal', 'true');
    content.setAttribute('aria-label', 'Product options');
    content.setAttribute('aria-hidden', 'true');
    trigger.setAttribute('aria-controls', content.id);
    trigger.setAttribute('aria-expanded', 'false');

    let open = false;

    const focusable = () => Array.from(content.querySelectorAll('button, input, select, textarea, a[href]'))
      .filter(element => !element.disabled && element.getClientRects().length > 0);

    const closeSheet = ({returnFocus = true} = {}) => {
      open = false;
      sticky.classList.remove('va-pdp-sticky-open');
      content.classList.remove('active');
      content.setAttribute('aria-hidden', 'true');
      trigger.setAttribute('aria-expanded', 'false');
      if (returnFocus) trigger.focus({preventScroll: true});
    };

    const openSheet = () => {
      if (!mobileQuery.matches) return;
      open = true;
      sticky.classList.add('va-pdp-sticky-open');
      content.classList.add('active');
      content.setAttribute('aria-hidden', 'false');
      trigger.setAttribute('aria-expanded', 'true');
      requestAnimationFrame(() => (focusable()[0] || close).focus({preventScroll: true}));
    };

    trigger.addEventListener('click', event => {
      if (!mobileQuery.matches) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      open ? closeSheet() : openSheet();
    }, true);
    close.addEventListener('click', () => closeSheet());

    document.addEventListener('keydown', event => {
      if (!open || !mobileQuery.matches) return;
      const otherModal = Array.from(document.querySelectorAll('dialog[open], [role="dialog"][aria-modal="true"]'))
        .some(dialog => dialog !== content && !content.contains(dialog) && !dialog.hidden && dialog.getAttribute('aria-hidden') !== 'true' && dialog.getClientRects().length > 0 && getComputedStyle(dialog).visibility !== 'hidden');
      if (otherModal) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        closeSheet();
        return;
      }
      if (event.key !== 'Tab') return;
      const items = focusable();
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }, true);

    window.addEventListener('resize', () => {
      if (!mobileQuery.matches && open) closeSheet({returnFocus: false});
    });
  };

  previous?.addEventListener('click', () => goTo(activeIndex - 1));
  next?.addEventListener('click', () => goTo(activeIndex + 1));
  gallery.addEventListener('keydown', event => {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      goTo(activeIndex - 1);
    }
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      goTo(activeIndex + 1);
    }
  });

  document.addEventListener('product:variant-change', event => {
    if (event.detail?.sectionId && String(event.detail.sectionId) !== String(sectionId)) return;
    const variant = event.detail?.variant;
    const color = variant?.options?.[colorIndex] || readColorFromPicker();
    applyColor(color);
  });

  applyColor(readColorFromPicker() || defaultColor);
  setupLightbox();
  setupQuantitySync();
  setupStickyOptions();
})();
